import { Group, Paper, Text, Stack, ThemeIcon } from '@mantine/core';
import { IconCircleCheck, IconX, IconQuestionMark, IconAlertTriangle } from '@tabler/icons-react';
import type { ScheduleGridSlotCell } from '../../../../services/api/cohortCoaches';

interface CoachScheduleSlotCellProps {
  slotCell: ScheduleGridSlotCell;
}

export function CoachScheduleSlotCell({ slotCell }: CoachScheduleSlotCellProps) {
  const { state } = slotCell;

  // Determine border and background based on state
  let bgColor = '#FFFFFF';
  let border = '1px solid #E8E6E1';

  if (state === 'scheduled') {
    bgColor = '#6C5CE7';
    border = '1.5px solid #6C5CE7';
  } else if (state === 'available') {
    bgColor = 'rgba(0,184,148,0.06)';
    border = '1.5px dashed #00B894';
  } else if (state === 'available_conditions') {
    bgColor = 'rgba(233,162,59,0.08)';
    border = '1.5px dashed #E9A23B';
  } else if (state === 'unavailable') {
    bgColor = '#F5F3EF';
    border = '1px solid #DFE6E9';
  } else if (state === 'awaiting_response') {
    bgColor = 'rgba(233, 162, 59, 0.1)';
  }

  return (
    <Paper
      p={4}
      shadow="0"
      style={{
        backgroundColor: bgColor,
        border,
        borderRadius: 4,
        minHeight: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Scheduled in this cohort */}
      {state === 'scheduled' && (
        <Stack gap={1} align="center">
          <Text size="xs" fw={600} c="white" ta="center" lineClamp={1}>
            {slotCell.group_name}
          </Text>
          {slotCell.conflict_date && (
            <Group gap={2} justify="center" wrap="nowrap">
              <IconAlertTriangle size={12} color="#E9A23B" />
              <Text size="xs" fw={500} c="#E9A23B" ta="center" style={{ fontSize: 10 }}>
                {formatConflictDate(slotCell.conflict_date)}
              </Text>
            </Group>
          )}
        </Stack>
      )}

      {/* Available (full) */}
      {state === 'available' && (
        <IconCircleCheck size={20} color="#00B894" />
      )}

      {/* Warning state: available with conditions (partial availability) */}
      {state === 'available_conditions' && (
        <Stack gap={1} align="center">
          <Group gap={2} justify="center" wrap="nowrap">
            <IconAlertTriangle size={16} color="#E9A23B" />
            {slotCell.conflict_date && (
              <Text size="xs" fw={500} c="#E9A23B" ta="center">
                {formatConflictDate(slotCell.conflict_date)}
              </Text>
            )}
          </Group>
        </Stack>
      )}

      {/* Unavailable */}
      {state === 'unavailable' && (
        <IconX size={18} color="#B2BEC3" />
      )}

      {/* Awaiting response */}
      {state === 'awaiting_response' && (
        <ThemeIcon size={20} radius="xl" color="orange" variant="light">
          <IconQuestionMark size={12} />
        </ThemeIcon>
      )}

      {/* Not requested */}
      {state === 'not_requested' && (
        <Text size="xs" c="#B2BEC3" ta="center">—</Text>
      )}
    </Paper>
  );
}

function formatConflictDate(isoDate: string): string {
  const dt = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}
